import { useMutation, type UseMutationOptions, useQueryClient } from "@tanstack/react-query";

import { BetterAuthError } from "../utils";
import { mcpFetch } from "./mcpFetch";
import {
  applicationTypeFor,
  MCP_CLIENT_GRANT_TYPES,
  MCP_CLIENT_RESPONSE_TYPES,
  MCP_CLIENT_SCOPES,
  mcpClientName,
  type McpClientWithSecret,
  mcpClientWithSecretSchema,
  PLACEHOLDER_REDIRECT_URI,
  type TokenEndpointAuthMethod,
} from "./schemas";
import { MCP_CLIENTS_QUERY_KEY } from "./useMcpClients";

export type CreateMcpClientInput = {
  /** The tool being connected, e.g. "Copilot Studio". */
  toolName: string;
  /** The caller's own company, which the server requires the registered name to end with. */
  companyName: string;
  tokenEndpointAuthMethod: TokenEndpointAuthMethod;
  /**
   * Omitted for a platform that only issues its callback URL after the connection exists, which is
   * the Copilot Studio case. The server registers a placeholder, and the URI is set afterwards.
   */
  redirectUris?: string[];
};

/**
 * Registers an MCP credential for the caller's company.
 *
 * The client-shape hook validates every field rather than choosing it, and refuses a body missing
 * one, so this sends the full MCP shape. The owning company and consent setting are still decided
 * server-side, and a field the hook doesn't expect is refused rather than ignored.
 */
export function useCreateMcpClient(
  options?: UseMutationOptions<McpClientWithSecret, BetterAuthError, CreateMcpClientInput, unknown>,
) {
  const queryClient = useQueryClient();
  const { onSuccess, ...rest } = options ?? {};

  return useMutation({
    ...rest,
    mutationFn: async ({ companyName, ...input }: CreateMcpClientInput) =>
      createClient(input, companyName),
    onSuccess: async (...args) => {
      await queryClient.invalidateQueries({
        queryKey: MCP_CLIENTS_QUERY_KEY,
        refetchType: "all",
      });
      onSuccess?.(...args);
    },
  });
}

async function createClient(
  input: Omit<CreateMcpClientInput, "companyName">,
  companyName: string,
): Promise<McpClientWithSecret> {
  const redirectUris = input.redirectUris?.length ? input.redirectUris : [PLACEHOLDER_REDIRECT_URI];
  return mcpFetch(
    "/oauth2/create-client",
    mcpClientWithSecretSchema,
    {
      method: "POST",
      body: {
        client_name: mcpClientName(input.toolName, companyName),
        token_endpoint_auth_method: input.tokenEndpointAuthMethod,
        scope: MCP_CLIENT_SCOPES.join(" "),
        grant_types: MCP_CLIENT_GRANT_TYPES,
        response_types: MCP_CLIENT_RESPONSE_TYPES,
        redirect_uris: redirectUris,
        application_type: applicationTypeFor(redirectUris),
        // Most platforms can't produce a DPoP proof, so tokens aren't bound to one.
        dpop_bound_access_tokens: false,
      },
    },
    "Could not create the credential",
  );
}
